"use client";

import { c as _c } from "react/compiler-runtime";
import { storage } from "@/lib/storage";
import { useSearchParams } from "next/navigation";
import { createContext, ReactNode, useCallback, useContext, useEffect, useReducer, useRef } from "react";
import type { FormAction, FormState, StepKey } from "../types/form";
const SCHEMA_VERSION = "v1";
// Use a prefix for dynamic keys
const LOCAL_STORAGE_PREFIX = "settlin-sell-form-state";
const getStorageKey = (id?: string | null) => {
  if (id) return `${LOCAL_STORAGE_PREFIX}-${id}`;
  return `${LOCAL_STORAGE_PREFIX}-draft`;
};
const initialState: FormState = {
  profile: {},
  address: {
    projectName: "",
    location: {},
    projectId: "",
    flatNumber: "",
    blockName: ""
  },
  basic: {},
  additional: {},
  other: {},
  uploads: {},
  fee: {
    agreedToTerms: false
  },
  slot: {},
  builderOrLandownerOrInvestorProject: {},
  schemaVersion: SCHEMA_VERSION
};
function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case "INIT":
      return {
        ...action.payload,
        schemaVersion: SCHEMA_VERSION
      };
    case "UPDATE_STEP_DATA":
      return {
        ...state,
        [action.payload.stepKey]: action.payload.data
      };
    case "MERGE_STEP_DATA":
      return {
        ...state,
        [action.payload.stepKey]: {
          ...(state[action.payload.stepKey] as object),
          ...(action.payload.data as object)
        }
      };
    case "RESET":
      return {
        ...initialState
      };
    default:
      return state;
  }
}
interface FormContextType {
  state: FormState;
  updateStepData: (stepKey: StepKey, data: unknown) => void;
  mergeStepData: (stepKey: StepKey, data: unknown) => void;
  resetForm: () => void;
}
const FormContext = createContext<FormContextType | undefined>(undefined);
interface FormProviderProps {
  children: ReactNode;
  initialData?: Partial<FormState>;
}
export function FormProvider(t0) {
  const $ = _c(32);
  const {
    children,
    initialData
  } = t0;
  const params = useSearchParams();
  let t1;
  if ($[0] !== params) {
    t1 = params?.get("rawUnitId");
    $[0] = params;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rawUnitId = t1;
  let t2;
  if ($[2] !== params) {
    t2 = params?.get("sellerUnitId");
    $[2] = params;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sellerUnitId = t2;
  let t3;
  if ($[4] !== params) {
    t3 = params?.get("groupId");
    $[4] = params;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const groupId = t3;
  const activeId = sellerUnitId || rawUnitId || groupId;
  const prevActiveIdRef = useRef(activeId);
  let t4;
  if ($[6] !== initialData) {
    t4 = {
      ...initialState,
      ...initialData
    };
    $[6] = initialData;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [state, dispatch] = useReducer(formReducer, t4);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (stepKey, data) => {
      dispatch({
        type: "UPDATE_STEP_DATA",
        payload: {
          stepKey,
          data
        }
      });
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const updateStepData = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (stepKey_0, data_0) => {
      dispatch({
        type: "MERGE_STEP_DATA",
        payload: {
          stepKey: stepKey_0,
          data: data_0
        }
      });
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const mergeStepData = t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      dispatch({
        type: "RESET"
      });
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const resetForm = t7;
  let t8;
  if ($[11] !== activeId || $[12] !== state) {
    t8 = async () => {
      try {
        if (typeof window !== "undefined") {
          const key = getStorageKey(activeId);
          await storage.setItem(key, state);
        }
      } catch {}
    };
    $[11] = activeId;
    $[12] = state;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const saveDraftToLocal = t8;
  let t9;
  if ($[14] !== activeId) {
    t9 = () => {
      const migrateLegacy = async () => {
        ;
        try {
          if (typeof window !== "undefined") {
            const legacyData = await storage.getItem(LOCAL_STORAGE_PREFIX);
            if (legacyData) {
              const draftKey = getStorageKey(null);
              const draftData = await storage.getItem(draftKey);
              if (!draftData) {
                await storage.setItem(draftKey, legacyData);
              }
              await storage.removeItem(LOCAL_STORAGE_PREFIX);
            }
          }
        } catch (t10) {
          const error = t10;
          console.error("Failed to persist sell form draft", {
            error,
            activeId
          });
        }
      };
      migrateLegacy();
    };
    $[14] = activeId;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== activeId) {
    t11 = () => {
      const prevId = prevActiveIdRef.current;
      if (prevId !== activeId) {
        if (prevId) {
          resetForm();
        }
        prevActiveIdRef.current = activeId;
      }
    };
    t12 = [activeId, resetForm];
    $[17] = activeId;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  if ($[20] !== saveDraftToLocal || $[21] !== state.address?.location || $[22] !== state.profile) {
    t13 = () => {
      if (Object.keys(state.profile || {}).length === 0 && Object.keys(state.address?.location || {}).length === 0) {
        return;
      }
      const timer = setTimeout(() => {
        saveDraftToLocal();
      }, 1000);
      return () => clearTimeout(timer);
    };
    $[20] = saveDraftToLocal;
    $[21] = state.address?.location;
    $[22] = state.profile;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== saveDraftToLocal || $[25] !== state) {
    t14 = [saveDraftToLocal, state];
    $[24] = saveDraftToLocal;
    $[25] = state;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  useEffect(t13, t14);
  let t15;
  if ($[27] !== state) {
    t15 = {
      state,
      updateStepData,
      mergeStepData,
      resetForm
    };
    $[27] = state;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const contextValue = t15;
  let t16;
  if ($[29] !== children || $[30] !== contextValue) {
    t16 = <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
    $[29] = children;
    $[30] = contextValue;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}
export function useFormContext() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
}
export async function loadDraftFromLocal(id?: string | null): Promise<FormState | null> {
  try {
    if (typeof window === "undefined") return null;

    // Try loading specific ID first
    let stored: FormState | null = null;
    if (id) {
      stored = await storage.getItem<FormState>(getStorageKey(id));
    }

    // Fallback to draft if no ID-specific data found (migration scenario)
    // Only migrate if we are transitioning/initializing
    if (!stored) {
      stored = await storage.getItem<FormState>(getStorageKey(null)); // load draft
    }
    if (!stored) return null;

    // Validate schema version
    if (stored.schemaVersion !== SCHEMA_VERSION) {
      // If schema is invalid, clear the stored data to prevent issues.
      if (id) {
        await storage.removeItem(getStorageKey(id));
      } else {
        await storage.removeItem(getStorageKey(null));
      }
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}
export async function clearDraftFromLocal(id?: string | null): Promise<void> {
  try {
    if (typeof window !== "undefined") {
      // If generic clear, clear the draft
      // If ID provided, clear that specific one
      const key = getStorageKey(id);
      await storage.removeItem(key);
    }
  } catch {}
}